import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';
import { AnatomicMapDirective } from './anatomicMapDirective/anatomic-map.directive';
import { IMultistageAnatomicMap } from '../models/IAnatomicMap';
import { IColorCode } from '../models/IColorCode';
import { IColorData } from '../models/IColorData';
import { ISegmentItem } from '../models/ISegmentItem';
import { ISelectionEvent } from '../models/ISelectionEvent';
import { DomSubsService } from '../services/domSubsService/domSubs.service';
import { SegmentLabels } from '@se/cp.common';

@Component({
    selector: 'anatomic-map',
    templateUrl: './map.component.html',
    styleUrls: ['./map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapComponent implements OnChanges, AfterViewInit, OnDestroy {
    @Input()
    public map: IMultistageAnatomicMap = null;

    @Input()
    public data: Array<IColorData>;

    @Input()
    public currentColor: IColorCode;

    @Input()
    public externalSegmentSelected: ISegmentItem | null;

    @Input()
    isMapSelected: boolean;

    @Input()
    public segmentLabel = SegmentLabels.none;

    @Input()
    public readOnly = false;

    @Input()
    public hasTooltips = true;

    @Input()
    public isWithViewId = false;

    @Input()
    public isWithStageName = false;


    @Input()
    public viewSideLenght = 200;

    @Input()
    public upperLabel: string | null = null

    @Input()
    public lowerLabel: string | null = null

    public boardFill = 'white';

    public get wrapperDimensions() {
        let dim = this.viewSideLenght - 20;
        const fontSize = dim / 11;
        return {
            "font-size": fontSize + 'px'
        };
    }

    @Output()
    public selectionEvent = new EventEmitter<ISelectionEvent>();

    @Output()
    public dataUpdated = new EventEmitter<IColorData>();

    @Output()
    public actionCompleted = new EventEmitter();

    @ViewChild(AnatomicMapDirective)
    private anatMapDirectiveRef: AnatomicMapDirective;

    @ViewChild('svgElContainer', { read: ElementRef })
    private container: ElementRef<HTMLDivElement>;

    private directiveSub: Subscription;

    private triggerSub: Subscription;



    constructor(private domSubsService: DomSubsService) { }

    ngOnChanges(changedProp: SimpleChanges) {
        if (changedProp.externalSegmentSelected) {
            this.onExternalSegmentSelChange();
        }

        if (this.isntFirstChange(changedProp.externalSegmentSelected)
            || this.isntFirstChange(changedProp.isMapSelected)
            || this.isntFirstChange(changedProp.data)
            || this.isntFirstChange(changedProp.segmentLabel)) {
            this.updateView();
        }
    }

    ngAfterViewInit() {
        this.updateView();
        if (!this.readOnly) {
            this.directiveSub = this.anatMapDirectiveRef.segmentSelected.subscribe((name) =>
                this.handleDirectiveSelection(name)
            );
        }
    }

    ngOnDestroy() {
        if (this.directiveSub) {
            this.directiveSub.unsubscribe();
        }
        if (this.triggerSub) {
            this.triggerSub.unsubscribe();
        }
        this.domSubsService.removeAllSubs();
    }

    public get svgMap() {
        const base = this.container.nativeElement.firstChild.cloneNode(true) as SVGElement;
        base.removeAttribute('anatomicMapDirective');
        return base;
    }

    private isntFirstChange(change: any): boolean {
        return change && !change.isFirstChange();
    }

    private updateView(): void {
        if (this.map) {
            this.anatMapDirectiveRef.loadOrUpdateMap(this.map, this.data, this.segmentLabel, this.hasTooltips);
        }
    }

    private onExternalSegmentSelChange() {
        if (!this.externalSegmentSelected) {
            this.deselectAllSegmentInMap();
            return;
        };
        this.deselectSegsIfNotExternal();
        const internal:ISegmentItem = this.findSegmentInMap(this.externalSegmentSelected.regionID);
        if (internal) {
            internal.isVisited = true;
            if (this.currentColor) {
                this.updateData(internal);
            }
        }
    }

    private handleDirectiveSelection(regionID?: string): void {
        const output: ISelectionEvent = { originMap: this.map };
        if (regionID) {
            output.segment = { ...this.findSegmentInMap(regionID) };
        }
        this.selectionEvent.emit(output);
    }

    private findSegmentInMap(regionID: string): ISegmentItem | undefined {
        return this.map.segmentCollection.find((s) => s.regionID === regionID);
    }

    private deselectSegsIfNotExternal(): void {
        const eventualInternalSegSelected = this.map.segmentCollection.find((s) => s.isVisited);
        if (eventualInternalSegSelected && eventualInternalSegSelected.regionID !== this.externalSegmentSelected.regionID) {
            eventualInternalSegSelected.isVisited = false;
            this.map.isVisited = false;
        }
    }

    private deselectAllSegmentInMap(){
        this.map.segmentCollection.forEach((segment: ISegmentItem)=> {
            segment.isVisited = false;
        })
    }

    private updateData(seg: ISegmentItem): void {
        if (seg) {
            const colorDataToUpdateIndex = this.data.findIndex((colorData) => colorData.regionID === seg.regionID);
            const dataUpdated:IColorData[] = [
                ...this.data.slice(0, colorDataToUpdateIndex),
                { ...this.currentColor, regionID: seg.regionID, segmentDescription: this.data[colorDataToUpdateIndex]?.segmentDescription },
                ...this.data.slice(colorDataToUpdateIndex + 1),
            ];
            if (this.isMapSelected) {
                // per limitare l'evento di cambiamento alla mappa di origine
                this.dataUpdated.emit({ ...dataUpdated[colorDataToUpdateIndex] });
            }
        }
    }


    public onMapContainerSelected() {
        this.anatMapDirectiveRef.onAnatomicMapClick();
    }
}
